import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import images from '../../assets/images';
import { AISearchOptimization } from '../../components/feature/AISearchOptimization';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add Google Fonts using useEffect
  useEffect(() => {
    // Add preconnect links
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    // Add font stylesheet
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    return () => {
      // Cleanup on unmount
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  const benefits = [
    { icon: 'ri-flashlight-line', title: 'Fast Delivery', description: 'Get your website live in as little as 7 days with our streamlined process', gradient: 'from-orange-400 to-pink-500' },
    { icon: 'ri-money-dollar-circle-line', title: 'Affordable Pricing', description: 'Professional quality at prices small businesses can afford', gradient: 'from-emerald-400 to-teal-500' },
    { icon: 'ri-star-line', title: 'Professional Results', description: 'Beautiful, modern designs that make your business stand out', gradient: 'from-yellow-400 to-orange-500' },
    { icon: 'ri-search-line', title: 'SEO-Ready', description: 'Built with search engine optimization from the ground up', gradient: 'from-cyan-400 to-blue-500' },
    { icon: 'ri-smartphone-line', title: 'Mobile Friendly', description: 'Perfect display on all devices, from phones to desktops', gradient: 'from-purple-400 to-pink-500' },
    { icon: 'ri-line-chart-line', title: 'High Conversions', description: 'Designed to turn visitors into customers effectively', gradient: 'from-green-400 to-emerald-500' },
  ];

  const services = [
    { 
      title: 'Website Design', 
      description: 'Custom websites tailored to your business needs',
      tag: 'Most Popular',
      gradient: 'from-blue-500 to-indigo-600',
      iconBg: 'bg-gradient-to-br from-blue-400 to-indigo-500',
      icon: 'ri-layout-line'
    },
    { 
      title: 'Premium Sites', 
      description: 'Advanced features and custom functionality',
      tag: 'Featured',
      gradient: 'from-purple-500 to-pink-600',
      iconBg: 'bg-gradient-to-br from-purple-400 to-pink-500',
      icon: 'ri-vip-crown-line'
    },
    { 
      title: 'Product Showcase Catalog', 
      description: 'Showcase your products beautifully',
      tag: 'Popular',
      gradient: 'from-teal-500 to-cyan-600',
      iconBg: 'bg-gradient-to-br from-teal-400 to-cyan-500',
      icon: 'ri-gallery-line'
    },
    { 
      title: 'E-Commerce', 
      description: 'Complete online store with checkout, product pages, and payment integration. Includes setup for your first 30 products',
      tag: 'Complete',
      gradient: 'from-orange-500 to-red-600',
      iconBg: 'bg-gradient-to-br from-orange-400 to-red-500',
      icon: 'ri-shopping-cart-line'
    },
    { 
      title: 'Maintenance Plans', 
      description: 'Keep your site updated and secure',
      tag: 'Ongoing',
      gradient: 'from-green-500 to-emerald-600',
      iconBg: 'bg-gradient-to-br from-green-400 to-emerald-500',
      icon: 'ri-tools-line'
    },
    { 
      title: 'SEO Optimization', 
      description: 'Rank higher on Google and AI search',
      tag: 'Rank Higher',
      gradient: 'from-yellow-500 to-orange-600',
      iconBg: 'bg-gradient-to-br from-yellow-400 to-orange-500',
      icon: 'ri-line-chart-line'
    },
  ];

  const industries = [
    { name: 'Restaurants', icon: 'ri-restaurant-line', color: 'from-red-400 to-orange-500' },
    { name: 'Salons', icon: 'ri-scissors-cut-line', color: 'from-pink-400 to-rose-500' },
    { name: 'Trades', icon: 'ri-tools-line', color: 'from-blue-400 to-cyan-500' },
    { name: 'Consultants', icon: 'ri-briefcase-line', color: 'from-purple-400 to-indigo-500' },
    { name: 'Lawyers', icon: 'ri-scales-3-line', color: 'from-gray-600 to-gray-800' },
    { name: 'Real Estate', icon: 'ri-home-4-line', color: 'from-green-400 to-emerald-500' },
    { name: 'Retail Shops', icon: 'ri-shopping-bag-line', color: 'from-yellow-400 to-amber-500' },
    { name: 'E-commerce', icon: 'ri-shopping-cart-line', color: 'from-teal-400 to-cyan-500' },
  ];

  const testimonials = [
    { name: 'Sarah Johnson', handle: '@sarahj', avatar: 'from-blue-400 to-blue-600', quote: 'BusinessWebsitePro transformed our online presence. We saw a 300% increase in inquiries within the first month!' },
    { name: 'Michael Chen', handle: '@mchen', avatar: 'from-green-400 to-green-600', quote: 'Affordable, professional, and fast. Exactly what my small business needed to compete online.' },
    { name: 'Emily Rodriguez', handle: '@emilyrod', avatar: 'from-purple-400 to-purple-600', quote: 'The team understood my vision perfectly. My new website has helped me attract premium clients consistently.' },
    { name: 'David Thompson', handle: '@dthompson', avatar: 'from-orange-400 to-orange-600', quote: 'Best investment I made for my business. The SEO optimization brought us to the top of local searches.' },
    { name: 'Lisa Martinez', handle: '@lisamart', avatar: 'from-pink-400 to-pink-600', quote: 'Professional quality at an unbeatable price. My customers love the new site and it shows in our sales.' },
    { name: 'James Wilson', handle: '@jwilson', avatar: 'from-indigo-400 to-indigo-600', quote: 'From consultation to launch, the process was smooth and efficient. Highly recommend for any small business owner.' },
  ];

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Vancouver Web Design | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional web design in Vancouver, BC. Transparent pricing, no hidden fees. Get a stunning website that you own 100%.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Zero Hidden Fees | Full Ownership & No Surprises | Our Transparent Pricing Policy | BWP');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Read our full commitment to transparency: zero hidden fees, guaranteed full website ownership, and no forced monthly plans. We build on trust, not tricks.');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Zero Hidden Fees | Full Ownership & No Surprises | Our Transparent Pricing Policy | BWP');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'Read our full commitment to transparency: zero hidden fees, guaranteed full website ownership, and no forced monthly plans. We build on trust, not tricks.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      
      {/* Enhanced Schema.org Organization structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://businesswebsitepro.com/#organization",
          "name": "BusinessWebsitePro",
          "alternateName": "Business Website Pro",
          "url": "https://businesswebsitepro.com",
          "logo": "https://businesswebsitepro.com/logo.png",
          "description": "Professional website design and development services for businesses in Vancouver, BC, Canada, and worldwide. Specializing in custom business websites, e-commerce solutions, and AI-powered search optimization.",
          "telephone": "+1-604-807-7862",
          "email": "info@businesswebsitepro.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Vancouver",
            "addressRegion": "BC",
            "addressCountry": "CA"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "49.2827",
            "longitude": "-123.1207"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "Vancouver",
              "containedIn": {
                "@type": "State",
                "name": "British Columbia"
              }
            },
            {
              "@type": "Country",
              "name": "Canada"
            },
            {
              "@type": "Country",
              "name": "United States"
            }
          ],
          "priceRange": "$399-$999",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "17:00"
          },
          "sameAs": [
            "https://facebook.com/businesswebsitepro",
            "https://instagram.com/businesswebsitepro",
            "https://linkedin.com/company/businesswebsitepro",
            "https://twitter.com/businesswebpro"
          ],
          "founder": {
            "@type": "Person",
            "name": "BusinessWebsitePro Founder"
          },
          "foundingDate": "2020",
          "slogan": "Innovative Design. No Hidden Fees."
        })}
      </script>

      {/* Schema.org LocalBusiness structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "BusinessWebsitePro",
          "description": "Professional website design and development services for businesses in Vancouver, BC, Canada, and worldwide. Specializing in custom business websites, e-commerce solutions, and AI-powered search optimization.",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://businesswebsitepro.com'}`,
          "telephone": "+1-604-807-7862",
          "email": "info@businesswebsitepro.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Vancouver",
            "addressRegion": "BC",
            "addressCountry": "CA"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "49.2827",
            "longitude": "-123.1207"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "Vancouver"
            },
            {
              "@type": "State",
              "name": "British Columbia"
            },
            {
              "@type": "Country",
              "name": "Canada"
            }
          ],
          "priceRange": "$399-$999",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday"
            ],
            "opens": "09:00",
            "closes": "17:00"
          },
          "sameAs": [
            "https://facebook.com/businesswebsitepro",
            "https://instagram.com/businesswebsitepro",
            "https://linkedin.com/company/businesswebsitepro",
            "https://twitter.com/businesswebpro"
          ]
        })}
      </script>

      {/* Schema.org WebSite with SearchAction */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "BusinessWebsitePro",
          "url": `${import.meta.env.VITE_SITE_URL || 'https://businesswebsitepro.com'}`,
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${import.meta.env.VITE_SITE_URL || 'https://businesswebsitepro.com'}/search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://businesswebsitepro.com/"
            }
          ]
        })}
      </script>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/2d22fa2dd98cdcab98ef4f1ec23cc841.png)'
          }}
        />
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>

        {/* No Upfront Payment Badge */}
        <div className="absolute top-32 right-8 z-20">
          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-8 py-4 rounded-xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-2xl"></i>
              <span className="font-bold text-lg whitespace-nowrap">No Upfront Payment</span>
            </div>
          </div>
        </div>

        {/* Glass Card - Centered */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6">
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
              Professional Websites with Transparent Pricing
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              No hidden fees. No surprises. Just beautiful, functional websites starting at $399 with guaranteed full ownership.
            </p>
            
            {/* CTA Buttons - Centered Below Text, moved 3 inches (~225px) left */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8" style={{ marginRight: '225px' }}>
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book Free Consultation
              </a>
              <a
                href="/portfolio"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-xl"></i>
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Card */}
      <div className="relative z-10 -mt-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-2xl shadow-2xl p-6 border border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Creative Design</h3>
                  <p className="text-white/90 text-xs leading-relaxed">Stunning visuals that capture your brand essence</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <i className="ri-rocket-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Fast Performance</h3>
                  <p className="text-white/90 text-xs leading-relaxed">Lightning-fast load times for better engagement</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">Transparent Pricing</h3>
                  <p className="text-white/90 text-xs leading-relaxed">Zero Hidden Fees - No Surprises</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Search Optimization Block - Moved After Hero */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <AISearchOptimization
          title="Professional Website Design Services for Small Businesses"
          summary="BusinessWebsitePro delivers transparent, affordable web design services with no hidden fees. We build custom websites that you own 100% - no monthly hosting fees, no surprise charges. Serving businesses in Vancouver, BC, across Canada, United States, and Worldwide."
          keyPoints={[
            "Transparent pricing with no hidden fees - what you see is what you pay",
            "Full website ownership - you own 100% of your website, code, and content",
            "Professional custom design tailored to your business needs",
            "Fast turnaround - most projects completed in 1-4 weeks",
            "Serving clients in Vancouver BC, across Canada, USA, and Worldwide"
          ]}
          entities={[
            {
              name: "BusinessWebsitePro",
              type: "Web Design Agency",
              description: "Professional web design agency serving businesses in Vancouver BC, across Canada, United States, and Worldwide. Specializing in transparent pricing and full client ownership. We build custom websites for small businesses, restaurants, salons, and service providers."
            },
            {
              name: "Full Website Ownership",
              type: "Business Model",
              description: "Unlike traditional agencies, we give you complete ownership. You get all source code, content, and assets. No monthly fees, no locked-in contracts. It's yours forever."
            },
            {
              name: "Transparent Pricing",
              type: "Pricing Model",
              description: "All costs disclosed upfront. Starter Package $399, Professional $599, Enterprise $999. No hidden fees, no surprise charges."
            }
          ]}
          faqs={[
            {
              question: "Do I really own 100% of my website?",
              answer: "Yes! Unlike most agencies, we give you complete ownership. You get all source code, content, and assets. No monthly fees, no locked-in contracts. It's yours forever."
            },
            {
              question: "Are there any hidden fees?",
              answer: "Absolutely not. We believe in complete transparency. The price you see is the price you pay. No setup fees, no monthly hosting fees, no surprise charges."
            },
            {
              question: "How long does it take to build a website?",
              answer: "Most projects are completed in 1-4 weeks depending on complexity. Starter packages typically take 1-2 weeks, Professional 2-3 weeks, and Enterprise 3-4 weeks."
            },
            {
              question: "Do you offer ongoing support after launch?",
              answer: "Yes! We offer flexible maintenance plans starting at $99/month. But it's completely optional - you're never locked in."
            }
          ]}
        />
      </div>

      {/* What You Need to Know Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50/50 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-wider text-[#14B8A6] font-semibold mb-4">WHAT YOU NEED TO KNOW</p>
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Transparent Pricing</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              No hidden fees. No surprises. Just beautiful, functional websites with guaranteed full ownership.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Starter Website */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Starter Website</h4>
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-5xl font-bold text-[#14B8A6]">$399</span>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">1-3 pages</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Mobile friendly design</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Contact form included</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Basic SEO optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">30 days support</span>
                </li>
              </ul>
            </div>

            {/* Business Website */}
            <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-teal-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                MOST POPULAR
              </div>
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Business Website</h4>
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-5xl font-bold text-[#14B8A6]">$599</span>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">3-5 pages</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Professional custom design</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Advanced SEO optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Google Maps integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">60 days support</span>
                </li>
              </ul>
            </div>

            {/* Premium Website */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Premium Website</h4>
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-5xl font-bold text-[#14B8A6]">$999</span>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">5-10 pages</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Advanced custom layout</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Gallery included (20 images)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">Blog page with 3 posts</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-700">90 days support</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line text-xl"></i>
              View Full Pricing
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50/50 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50/40 via-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-wider text-[#14B8A6] font-semibold mb-4">OUR SERVICES</p>
            <h3 className="text-4xl font-bold text-gray-900">Everything Your Business Needs Online</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`h-2 bg-gradient-to-r ${service.gradient}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 ${service.iconBg} rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${service.icon} text-2xl text-white`}></i>
                    </div>
                    <span className={`bg-gradient-to-r ${service.gradient} text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-md`}>
                      {service.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50/30 via-white to-pink-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h3>
            <p className="text-xl text-gray-600">Tailored solutions for every business type</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-8 flex flex-col items-center justify-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${industry.icon} text-3xl text-white`}></i>
                </div>
                <p className="text-base font-semibold text-gray-900 text-center">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50/30 via-white to-teal-50/40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="inline-block bg-gradient-to-r from-[#14B8A6] to-[#0d9488] text-white text-xs font-semibold px-4 py-2 rounded-full mb-4 shadow-md">
              TESTIMONIALS
            </span>
            <h3 className="text-4xl font-bold text-gray-900">What Our Clients Say</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-7 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 bg-gradient-to-br ${testimonial.avatar} rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.handle}</p>
                  </div>
                </div>
                <div className="relative">
                  <i className="ri-double-quotes-l text-4xl text-[#14B8A6] opacity-20 absolute -top-2 -left-2"></i>
                  <p className="text-base text-gray-700 leading-relaxed relative z-10">
                    {testimonial.quote}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}